import { useEffect, useRef, useState } from "react";
import { Eye, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  fetchEmailTemplates, updateEmailTemplate, resetEmailTemplate,
  previewEmailTemplate, sendTestEmail,
} from "@/lib/api";

export default function EmailTemplatesPanel({ adminEmail }) {
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchEmailTemplates();
      setTemplates(data);
      setActiveKey((prev) => prev ?? (data.length ? data[0].key : null));
    } catch (e) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const active = templates.find((t) => t.key === activeKey);

  if (loading) {
    return <div className="text-sm text-muted-foreground py-12 text-center">Loading email templates…</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
      <aside className="space-y-1.5" data-testid="email-template-list">
        {templates.map((t) => {
          const isActive = t.key === activeKey;
          return (
            <button
              key={t.key}
              onClick={() => setActiveKey(t.key)}
              data-testid={`email-template-item-${t.key}`}
              className={`w-full text-left px-4 py-3 rounded-2xl border transition-all ${
                isActive ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-sm font-medium leading-snug">{t.name}</span>
                {t.is_customized && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200 whitespace-nowrap">Custom</span>
                )}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t.recipient_role === "admin" ? "To admin" : "To buyer"}
              </div>
            </button>
          );
        })}
      </aside>

      {active && (
        <EmailTemplateEditor
          key={`${active.key}:${active.subject}:${active.title}:${active.body_html}`}
          template={active}
          adminEmail={adminEmail}
          onChanged={load}
        />
      )}
    </div>
  );
}

function EmailTemplateEditor({ template, adminEmail, onChanged }) {
  const [draft, setDraft] = useState({
    subject: template.subject,
    title: template.title,
    body_html: template.body_html,
  });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [testTo, setTestTo] = useState(adminEmail || "");
  const [sendingTest, setSendingTest] = useState(false);
  const bodyRef = useRef(null);

  const isDirty =
    draft.subject !== template.subject ||
    draft.title !== template.title ||
    draft.body_html !== template.body_html;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateEmailTemplate(template.key, draft);
      toast.success("Template saved");
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset this template back to the original Rowen default? Your customizations will be lost.")) return;
    setSaving(true);
    try {
      await resetEmailTemplate(template.key);
      toast.success("Reset to default");
      await onChanged();
    } catch (e) {
      toast.error("Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      if (isDirty) await updateEmailTemplate(template.key, draft);
      const res = await previewEmailTemplate(template.key);
      setPreviewHtml(res.html);
      setPreviewSubject(res.subject);
      if (isDirty) await onChanged();
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const handleSendTest = async () => {
    if (!testTo.trim()) return;
    setSendingTest(true);
    try {
      if (isDirty) await updateEmailTemplate(template.key, draft);
      const res = await sendTestEmail(template.key, testTo.trim());
      toast.success(`Test email sent to ${res.to}`);
      if (isDirty) await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test (check Resend domain verification)");
    } finally {
      setSendingTest(false);
    }
  };

  const insertVariable = (varName) => {
    const textarea = bodyRef.current;
    const placeholder = `{${varName}}`;
    if (!textarea) {
      setDraft((d) => ({ ...d, body_html: d.body_html + placeholder }));
      return;
    }
    const start = textarea.selectionStart ?? draft.body_html.length;
    const end = textarea.selectionEnd ?? draft.body_html.length;
    const next = draft.body_html.slice(0, start) + placeholder + draft.body_html.slice(end);
    setDraft({ ...draft, body_html: next });
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + placeholder.length;
    });
  };

  return (
    <div className="rounded-3xl border border-border bg-background p-6 md:p-8 space-y-6" data-testid="email-template-editor">
      <div>
        <h3 className="font-serif text-2xl">{template.name}</h3>
        <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
          Available variables — click to insert into the body
        </p>
        <div className="flex flex-wrap gap-2">
          {template.variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVariable(v)}
              data-testid={`email-var-${v}`}
              className="px-3 py-1 rounded-full bg-foreground/5 hover:bg-foreground/10 text-xs font-mono"
            >
              {`{${v}}`}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Subject line</label>
        <input
          type="text"
          value={draft.subject}
          onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          data-testid="email-template-subject"
          className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">In-email heading</label>
        <input
          type="text"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          data-testid="email-template-title"
          className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">
          Body — HTML allowed (e.g. &lt;p&gt;, &lt;strong&gt;, &lt;br&gt;)
        </label>
        <textarea
          ref={bodyRef}
          rows={14}
          value={draft.body_html}
          onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
          data-testid="email-template-body"
          spellCheck="true"
          className="w-full px-5 py-4 rounded-2xl border border-border bg-transparent focus:outline-none focus:border-primary text-sm font-mono leading-relaxed"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          data-testid="email-template-save"
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm disabled:opacity-50 hover:-translate-y-0.5 transition-transform"
        >
          {saving ? "Saving…" : isDirty ? "Save changes" : "Saved"}
        </button>
        <button
          type="button"
          onClick={handlePreview}
          data-testid="email-template-preview"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border text-sm hover:bg-foreground/5"
        >
          <Eye size={14} strokeWidth={1.5} /> Preview with sample data
        </button>
        {template.is_customized && (
          <button
            type="button"
            onClick={handleReset}
            data-testid="email-template-reset"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border text-sm text-muted-foreground hover:bg-foreground/5"
          >
            <RotateCcw size={14} strokeWidth={1.5} /> Reset to default
          </button>
        )}
      </div>

      <div className="pt-2 border-t border-border">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Send a test</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Recipient email"
            data-testid="email-template-test-to"
            className="flex-1 px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm"
          />
          <button
            type="button"
            onClick={handleSendTest}
            disabled={sendingTest || !testTo.trim()}
            data-testid="email-template-send-test"
            className="inline-flex items-center gap-2 justify-center px-5 py-3 rounded-full bg-foreground/90 text-background text-sm disabled:opacity-50 hover:-translate-y-0.5 transition-transform"
          >
            <Send size={14} strokeWidth={1.5} /> {sendingTest ? "Sending…" : "Send test"}
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
          Note: Resend will only deliver to a verified address until you verify <strong>rowenkidsclub.com</strong> at{" "}
          <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a>.
        </p>
      </div>

      {previewHtml && (
        <div className="pt-4 border-t border-border" data-testid="email-template-preview-pane">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
            Preview · Subject: <span className="text-foreground normal-case tracking-normal">{previewSubject}</span>
          </p>
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            className="w-full rounded-2xl border border-border bg-white"
            style={{ height: 700 }}
          />
        </div>
      )}
    </div>
  );
}
